import { darken } from '@mui/material/styles';

const WHITE = '#ffffff';
const BLACK = '#000000';
const MAX_DARKEN_STEPS = 20;
const DARKEN_STEP = 0.08;

/** WCAG 2.1 AA minimum contrast for normal-sized text. */
export const WCAG_AA_TEXT_RATIO = 4.5;

/** Slightly above 4.5 to absorb rounding differences between runtime math and axe. */
export const WCAG_AA_SAFE_RATIO = 4.51;

type Rgb = { r: number; g: number; b: number };

function linearize(channel: number): number {
  const normalized = channel / 255;
  return normalized <= 0.03928
    ? normalized / 12.92
    : ((normalized + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance({ r, g, b }: Rgb): number {
  return 0.2126 * linearize(r) + 0.7152 * linearize(g) + 0.0722 * linearize(b);
}

function parseRgbChannels(color: string): Rgb {
  const rgbMatch = color.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbMatch) {
    return {
      r: Number(rgbMatch[1]),
      g: Number(rgbMatch[2]),
      b: Number(rgbMatch[3]),
    };
  }

  const hex = color.replace('#', '');
  return {
    r: parseInt(hex.slice(0, 2), 16),
    g: parseInt(hex.slice(2, 4), 16),
    b: parseInt(hex.slice(4, 6), 16),
  };
}

function toHex({ r, g, b }: Rgb): string {
  const channel = (value: number) => value.toString(16).padStart(2, '0');
  return `#${channel(r)}${channel(g)}${channel(b)}`;
}

/** WCAG 2.1 contrast ratio — matches axe color-contrast calculations. */
export function getWcagContrastRatio(foreground: string, background: string): number {
  const fgLuminance = relativeLuminance(parseRgbChannels(foreground));
  const bgLuminance = relativeLuminance(parseRgbChannels(background));
  const lighter = Math.max(fgLuminance, bgLuminance);
  const darker = Math.min(fgLuminance, bgLuminance);

  return (lighter + 0.05) / (darker + 0.05);
}

export function meetsContrastRatio(
  foreground: string,
  background: string,
  ratio = WCAG_AA_TEXT_RATIO,
): boolean {
  return getWcagContrastRatio(foreground, background) >= ratio;
}

/** Picks white or black foreground that meets the contrast ratio on the given background. */
export function getAccessibleContrastText(
  background: string,
  ratio = WCAG_AA_TEXT_RATIO,
): string {
  const whiteRatio = getWcagContrastRatio(WHITE, background);
  const blackRatio = getWcagContrastRatio(BLACK, background);

  const whitePasses = whiteRatio >= ratio;
  const blackPasses = blackRatio >= ratio;

  if (whitePasses && blackPasses) {
    return whiteRatio >= blackRatio ? WHITE : BLACK;
  }
  if (whitePasses) return WHITE;
  if (blackPasses) return BLACK;

  return whiteRatio >= blackRatio ? WHITE : BLACK;
}

/** Blends a foreground color over a solid background at the given alpha (0–1). */
export function blendOnBackground(color: string, alpha: number, background: string): string {
  const fg = parseRgbChannels(color);
  const bg = parseRgbChannels(background);

  const blend = (fgChannel: number, bgChannel: number) =>
    Math.round(fgChannel * alpha + bgChannel * (1 - alpha));

  return toHex({
    r: blend(fg.r, bg.r),
    g: blend(fg.g, bg.g),
    b: blend(fg.b, bg.b),
  });
}

/** Darkens a foreground color until it meets the contrast ratio on the background. */
export function getAccessibleColorOnBackground(
  foreground: string,
  background: string,
  ratio = WCAG_AA_SAFE_RATIO,
): string {
  let color = foreground;

  for (let step = 0; step < MAX_DARKEN_STEPS; step += 1) {
    color = step === 0 ? color : darken(color, DARKEN_STEP);
    if (meetsContrastRatio(color, background, ratio)) {
      return toHex(parseRgbChannels(color));
    }
  }

  return toHex(parseRgbChannels(color));
}

/** Blends a color over white at the given alpha (0–1). */
export function blendOnWhite(color: string, alpha: number): string {
  return blendOnBackground(color, alpha, WHITE);
}
